// Персистентный кэш списков viewer в localStorage, per-user, на этом устройстве
// (тот же посыл, что и recentDestinations.ts). Позволяет после перезагрузки страницы
// показать последнюю просмотренную папку/раздел мгновенно, без спиннера, а затем тихо
// обновить её в фоне (SWR) — см. loadWithPagination и listeners.ts.
//
// Хранение поэлементное: индекс с метаданными лежит под одним ключом, каждая локация —
// под своим. Раньше весь кэш жил одной строкой, и любая подгруженная страница заставляла
// сериализовать все локации целиком (тысячи объектов Asset) — синхронно, на главном потоке,
// ровно в момент скролла. Теперь запись одной локации стоит одну её страницу.
//
// В localStorage кладём облегчённый снапшот: только первую страницу ассетов каждой локации
// (её размер приходит с бэкенда, см. pageSize) — это ограничивает размер и делает
// настоящим инвариант «в персисте ровно одна страница», на который опирается фоновый рефетч.

import { captureException } from '@myua/api';
import { Asset, TotalInfo } from '../../api';
import { AssetsCache, CacheEntry } from '../finder/model';
import { ViewerData } from './model';

const STORAGE_PREFIX = 'assetLab.viewer.listCache';
// Версия схемы: при несовместимом изменении формы Asset/записи — поднять, чтобы
// старые записи из localStorage игнорировались.
const VERSION = 2;
// Сколько живёт снапшот. Отсчёт идёт от момента, когда данные пришли из сети (storedAt),
// а не от последнего визита в приложение. Сутки, а не неделя: имена файлов и подписанные
// ссылки на превью — не то, что стоит держать на диске общей машины дольше нужного, а
// пользы от снапшота недельной давности нет, он всё равно весь уйдёт в ре-валидейт.
const TTL_MS = 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 20; // не более 20 локаций в персисте
// Страхует случай, когда pageSize неизвестен (запись из старой сессии): столько ассетов
// сохраняем максимум.
const FALLBACK_MAX_ASSETS = 60;

type IndexRecord = {
  key: string; // ключ локации (buildCacheKey)
  slot: string; // суффикс ключа localStorage, где лежат сами ассеты
  savedAt: number; // когда данные пришли из сети (storedAt записи)
  hasNextPage: boolean;
  scrollHash: string;
  pageSize?: number;
};

// Порядок записей в индексе — порядок обращения, свежие в конце (как в кэше в памяти).
type StoredIndex = { version: number; entries: IndexRecord[] };

type StoredSlot = { key: string; assets: Asset[]; total: TotalInfo };

// Персист отключается на сессию, если localStorage переполнен и самоочистка не помогла:
// смысла долбить его на каждой подгруженной странице нет.
let disabled = false;
let quotaReported = false;

function indexKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId || 'anon'}`;
}

function slotKey(userId: string, slot: string): string {
  return `${indexKey(userId)}.${slot}`;
}

// Короткий детерминированный идентификатор локации: ключ кэша — это JSON фильтров, в имя
// ключа localStorage он не годится. Коллизия хешей не страшна: в слоте лежит и сам ключ,
// на чтении он сверяется (см. loadPersistedViewerCache).
function slotFor(key: string): string {
  let hash = 5381;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 33) ^ key.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

function isQuotaError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

// Переполнение квоты незаметно ломает фичу: кэш просто перестаёт переживать перезагрузку,
// и снаружи это выглядит как «иногда мгновенно, иногда нет». Сообщаем один раз за сессию.
function reportQuota(error: unknown): void {
  if (quotaReported) {
    return;
  }
  quotaReported = true;
  // level не задаём: тип ScopeContext в подтянутой версии @sentry/types ждёт enum Severity,
  // а тащить его сюда ради одного поля незачем — событие и так одно за сессию.
  captureException(error instanceof Error ? error : new Error('localStorage quota exceeded'), {
    tags: { area: 'assetLab.viewer.listCache' },
  });
}

function readIndex(userId: string): IndexRecord[] {
  try {
    const raw = localStorage.getItem(indexKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return [];
    const payload = parsed as Partial<StoredIndex>;
    if (payload.version !== VERSION || !Array.isArray(payload.entries)) {
      return [];
    }
    return payload.entries.filter(
      (entry): entry is IndexRecord =>
        !!entry &&
        typeof entry === 'object' &&
        typeof entry.key === 'string' &&
        typeof entry.slot === 'string' &&
        typeof entry.savedAt === 'number' &&
        typeof entry.hasNextPage === 'boolean' &&
        typeof entry.scrollHash === 'string',
    );
  } catch {
    return [];
  }
}

function writeIndex(userId: string, entries: IndexRecord[]): void {
  localStorage.setItem(indexKey(userId), JSON.stringify({ version: VERSION, entries }));
}

function readSlot(userId: string, slot: string): StoredSlot | undefined {
  try {
    const raw = localStorage.getItem(slotKey(userId, slot));
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return undefined;
    const payload = parsed as Partial<StoredSlot>;
    if (typeof payload.key !== 'string' || !Array.isArray(payload.assets) || !payload.total) {
      return undefined;
    }
    return payload as StoredSlot;
  } catch {
    return undefined;
  }
}

function dropSlot(userId: string, slot: string): void {
  try {
    localStorage.removeItem(slotKey(userId, slot));
  } catch {
    // Хранилище недоступно — ничего страшного, запись просто останется мусором.
  }
}

// Читает кэш локаций для пользователя. Отбрасывает протухшие по TTL записи (и подчищает
// их слоты). Каждая валидная запись помечается revalidate: true — при первом заходе будет
// тихо обновлена из сети (данные/подписанные превью могли устареть за сессию).
// Возвращает {} если хранилище недоступно, версия иная или ничего валидного нет.
export function loadPersistedViewerCache(userId: string): AssetsCache<ViewerData> {
  try {
    const entries = readIndex(userId);
    if (entries.length === 0) return {};

    const now = Date.now();
    const result: AssetsCache<ViewerData> = {};
    const kept: IndexRecord[] = [];

    entries.forEach((record) => {
      if (now - record.savedAt > TTL_MS) {
        dropSlot(userId, record.slot);
        return;
      }
      const slot = readSlot(userId, record.slot);
      // Сверяем ключ: слот мог быть перезаписан другой локацией с тем же хешем.
      if (!slot || slot.key !== record.key) {
        dropSlot(userId, record.slot);
        return;
      }
      kept.push(record);
      result[record.key] = {
        data: { assets: slot.assets, total: slot.total },
        hasNextPage: record.hasNextPage,
        scrollHash: record.scrollHash,
        pageSize: record.pageSize,
        // storedAt — настоящее время получения данных, поэтому TTL честный и после гидрации.
        storedAt: record.savedAt,
        revalidate: true,
      };
    });

    if (kept.length !== entries.length) {
      writeIndex(userId, kept);
    }
    return result;
  } catch {
    return {};
  }
}

// Сохраняет одну локацию: её слот плюс индекс (индекс маленький — только метаданные).
// Запись обрезается до первой страницы, кэш — до MAX_ENTRIES локаций (лишние слоты удаляются).
export function savePersistedViewerEntry(
  userId: string,
  key: string,
  entry: CacheEntry<ViewerData>,
): void {
  if (disabled) {
    return;
  }
  const write = () => {
    const slot = slotFor(key);
    const limit = entry.pageSize && entry.pageSize > 0 ? entry.pageSize : FALLBACK_MAX_ASSETS;
    const assets = entry.data.assets.slice(0, limit);

    const payload: StoredSlot = { key, assets, total: entry.data.total };
    localStorage.setItem(slotKey(userId, slot), JSON.stringify(payload));

    const record: IndexRecord = {
      key,
      slot,
      savedAt: entry.storedAt ?? Date.now(),
      // Если ассетов было больше, чем сохранили — следующая страница гарантированно есть.
      hasNextPage: entry.hasNextPage || entry.data.assets.length > assets.length,
      scrollHash: entry.scrollHash,
      pageSize: entry.pageSize,
    };

    // Обновляемая локация уходит в конец: порядок индекса — порядок обращения.
    const entries = readIndex(userId).filter((item) => item.key !== key);
    entries.push(record);
    while (entries.length > MAX_ENTRIES) {
      const evicted = entries.shift();
      if (evicted && evicted.slot !== slot) {
        dropSlot(userId, evicted.slot);
      }
    }
    writeIndex(userId, entries);
  };

  try {
    write();
  } catch (error) {
    if (!isQuotaError(error)) {
      // Хранилище недоступно (приватный режим, отключённое хранилище) — молча пропускаем:
      // кэш остаётся только в памяти на текущую сессию.
      return;
    }
    // Квота переполнена: чистим свой кэш целиком и пробуем сохранить только текущую
    // локацию. Не вышло — выключаем персист на сессию, чтобы не писать в пустоту.
    reportQuota(error);
    clearPersistedViewerCache(userId);
    try {
      write();
    } catch {
      disabled = true;
    }
  }
}

// Удаляет одну локацию (её данные больше не соответствуют серверу).
export function removePersistedViewerEntry(userId: string, key: string): void {
  try {
    const entries = readIndex(userId);
    const target = entries.find((item) => item.key === key);
    if (!target) {
      return;
    }
    dropSlot(userId, target.slot);
    writeIndex(
      userId,
      entries.filter((item) => item.key !== key),
    );
  } catch {
    // Хранилище недоступно — запись переживёт перезагрузку, но при заходе будет
    // ре-валидирована, так что критичного расхождения нет.
  }
}

// Удаляет весь персистентный кэш пользователя (индекс и все слоты).
export function clearPersistedViewerCache(userId: string): void {
  try {
    readIndex(userId).forEach((record) => dropSlot(userId, record.slot));
    localStorage.removeItem(indexKey(userId));
  } catch {
    // Хранилище недоступно — чистить нечего.
  }
}

// Удаляет кэши других пользователей этого устройства. Логаута в SPA нет (сессия живёт в
// SSO), поэтому единственный надёжный момент — загрузка текущего пользователя: если за
// компьютером сменился человек, имена файлов и ссылки предыдущего уходят с диска.
export function dropForeignViewerCaches(userId: string): void {
  try {
    const own = indexKey(userId);
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      // Сравниваем строго: у пользователей 1 и 12 префиксы иначе накладывались бы.
      const isOwn = key === own || key?.startsWith(`${own}.`);
      if (key && key.startsWith(`${STORAGE_PREFIX}.`) && !isOwn) {
        doomed.push(key);
      }
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Хранилище недоступно — чистить нечего.
  }
}
